import { DataService } from './data-service';
import {
  TransferOrder,
  TransferOrderApiResponse,
  TransferOrdersApiResponse,
  TransferOrderCreatePayload,
  AllocationRequest,
  AllocationResponse,
  PickingRequest,
  PickingResponse,
  ShippingRequest,
  ShippingResponse,
  ReceivingRequest,
  ReceivingResponse,
} from '../types/TransferOrder.types';

/**
 * Service for Transfer Order API operations
 * Provides centralized access to all transfer order endpoints
 */
export class TransferOrderService {
  /**
   * Transform API response (camelCase) to frontend format (PascalCase)
   */
  private static transformOrder(order: any): TransferOrder {
    return {
      Id: order.id,
      ClientId: order.clientId,
      ClientName: order.clientName,
      OrderName: order.name,
      Status: order.status,
      Items: order.items || [],
      Records: order.records,
      ItemCount: order.itemCount,
      TotalQuantity: order.totalQuantity,
      TotalRequestedQuantity: order.totalRequestedQuantity,
      TotalAllocatedQuantity: order.totalAllocatedQuantity,
      TotalPickedQuantity: order.totalPickedQuantity,
      TotalShippedQuantity: order.totalShippedQuantity,
      TotalReceivedQuantity: order.totalReceivedQuantity,
      DeliveryAreaStorageUnitId: order.deliveryAreaStorageUnitId,
      Notes: order.notes,
      CreatedAt: order.createdAt,
      UpdatedAt: order.updatedAt,
      AllocatedAt: order.allocatedAt,
      PickedAt: order.pickedAt,
      ShippedAt: order.shippedAt,
      ReceivedAt: order.receivedAt,
      CompletedAt: order.completedAt,
    };
  }

  /**
   * Get all transfer orders with optional filters
   * GET /api/TransferOrder?status={status}&clientId={clientId}
   */
  static async getAll(filters?: { status?: number; clientId?: string }): Promise<TransferOrder[]> {
    const queryParams = new URLSearchParams();
    if (filters?.status !== undefined) {
      queryParams.append('status', String(filters.status));
    }
    if (filters?.clientId) {
      queryParams.append('clientId', filters.clientId);
    }

    const url = queryParams.toString() ? `api/TransferOrder?${queryParams}` : 'api/TransferOrder';
    const res = await DataService.get(url);
    const response: TransferOrdersApiResponse = await res.json();
    const orders = response.data || [];
    return orders.map(this.transformOrder);
  }

  /**
   * Get a single transfer order by ID
   * GET /api/TransferOrder/{id}
   */
  static async getById(orderId: string): Promise<TransferOrder> {
    const res = await DataService.get(`api/TransferOrder/${orderId}`);
    const response = await res.json();
    const order = response.data || response;
    return this.transformOrder(order);
  }

  /**
   * Create a new transfer order
   * POST /api/TransferOrder/create
   */
  static async create(payload: TransferOrderCreatePayload): Promise<TransferOrder> {
    const res = await DataService.post('api/TransferOrder/create', payload);
    const response = await res.json();
    const order = response.data || response;
    return this.transformOrder(order);
  }

  /**
   * Allocate items to a transfer order
   * POST /api/TransferOrder/allocate
   */
  static async allocate(payload: AllocationRequest): Promise<AllocationResponse> {
    const res = await DataService.post('api/TransferOrder/allocate', payload);
    const response = await res.json();

    if (!res.ok || !response.success) {
      throw new Error(response.message || 'Allocation failed');
    }

    return response;
  }

  /**
   * Pick items from a transfer order
   * POST /api/TransferOrder/pick
   */
  static async pick(payload: PickingRequest): Promise<PickingResponse> {
    const res = await DataService.post('api/TransferOrder/pick', payload);
    const response = await res.json();

    if (!res.ok || !response.success) {
      throw new Error(response.message || 'Picking failed');
    }

    return response;
  }

  /**
   * Ship a transfer order
   * POST /api/TransferOrder/ship
   */
  static async ship(payload: ShippingRequest): Promise<ShippingResponse> {
    const res = await DataService.post('api/TransferOrder/ship', payload);
    const response = await res.json();

    if (!res.ok || !response.success) {
      throw new Error(response.message || 'Shipping failed');
    }

    return response;
  }

  /**
   * Receive items from a transfer order (client side)
   * POST /api/operation/client-receive
   */
  static async receive(payload: ReceivingRequest): Promise<ReceivingResponse> {
    const res = await DataService.post('api/operation/client-receive', payload);
    const response = await res.json();

    if (!res.ok || !response.success) {
      throw new Error(response.message || 'Receiving failed');
    }

    return response;
  }

  /**
   * Update transfer order status
   * PATCH /api/TransferOrder/{id}/status
   */
  static async updateStatus(orderId: string, status: number): Promise<void> {
    const res = await DataService.patch(`api/TransferOrder/${orderId}/status`, { status });
    const response = await res.json();

    if (!res.ok || !response.success) {
      throw new Error(response.message || 'Status update failed');
    }
  }

  /**
   * Delete a transfer order
   * DELETE /api/TransferOrder/{id}
   */
  static async delete(orderId: string): Promise<void> {
    const res = await DataService.delete(`api/TransferOrder/${orderId}`, {});
    const response = await res.json();

    if (!res.ok || !response.success) {
      throw new Error(response.message || 'Delete failed');
    }
  }
}
